<template>
  <section class="job-section" v-if="jobs.length > 0">
    <div class="section-header">
      <div class="title-area">
        <span class="section-icon new">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </span>
        <h2 class="section-title">신규 공고</h2>
        <span class="job-count">{{ jobs.length }}</span>
      </div>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-grid">
        <div v-for="i in 4" :key="i" class="skeleton-card">
          <div class="skeleton-thumb"></div>
          <div class="skeleton-content">
            <div class="skeleton-tags"></div>
            <div class="skeleton-title"></div>
            <div class="skeleton-text"></div>
            <div class="skeleton-salary"></div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- 데스크톱: 그리드 -->
      <div class="job-grid">
        <router-link 
          v-for="job in jobs" 
          :key="job.id" 
          :to="`/jobs/${job.id}`" 
          class="job-card"
        >
          <div class="card-thumbnail">
            <img 
              v-if="job.thumbnail || job.images?.length" 
              :src="job.thumbnail || job.images?.[0]" 
              :alt="job.siteName || job.title" 
            />
            <div v-else class="no-image">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <!-- 뱃지 영역 -->
            <div class="card-badges">
              <span v-if="job.isPremium" class="badge premium">PREMIUM</span>
              <span v-if="job.isPick" class="badge pick">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                PICK
              </span>
              <span v-if="job.isUrgent" class="badge urgent">급구</span>
              <span class="badge new">NEW</span>
              <span v-if="job.isFeatured" class="badge featured">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                </svg>
              </span>
            </div>
            <button class="bookmark-btn" @click.prevent="handleToggleBookmark(job)">
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                :fill="isBookmarked(job.id) ? 'currentColor' : 'none'" 
                stroke="currentColor" 
                stroke-width="2"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          </div>
          <div class="card-content">
            <div class="card-tags">
              <span class="tag region">{{ job.region }}</span>
              <span class="tag industry">{{ job.industry }}</span>
            </div>
            <h3 class="card-title">{{ job.siteName || job.title }}</h3>
            <p class="card-job-type">{{ job.jobType }}</p>
            <div class="card-footer">
              <div class="card-salary">{{ job.salaryType }} {{ job.salary }}</div>
              <span class="card-date">{{ formatDate(job.createdAt) }}</span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- 모바일: 슬라이드 -->
      <div class="job-slider" ref="sliderRef" @scroll="handleScroll">
        <div class="slider-track">
          <router-link 
            v-for="job in jobs" 
            :key="'mobile-' + job.id" 
            :to="`/jobs/${job.id}`" 
            class="job-card"
          >
            <div class="card-thumbnail">
              <img 
                v-if="job.thumbnail || job.images?.length" 
                :src="job.thumbnail || job.images?.[0]" 
                :alt="job.siteName || job.title" 
              />
              <div v-else class="no-image">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
              <!-- 모바일 뱃지 -->
              <div class="card-badges">
                <span v-if="job.isPremium" class="badge premium">PREMIUM</span>
                <span v-if="job.isPick" class="badge pick">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  PICK
                </span>
                <span v-if="job.isUrgent" class="badge urgent">급구</span>
                <span class="badge new">NEW</span>
                <span v-if="job.isFeatured" class="badge featured">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                  </svg>
                </span>
              </div>
            </div>
            <div class="card-content">
              <div class="card-tags">
                <span class="tag region">{{ job.region }}</span>
                <span class="tag industry">{{ job.industry }}</span>
              </div>
              <h3 class="card-title">{{ job.siteName || job.title }}</h3>
              <p class="card-job-type">{{ job.jobType }}</p>
              <div class="card-salary">{{ job.salaryType }} {{ job.salary }}</div>
            </div>
          </router-link>
        </div>
      </div>

      <!-- 모바일 인디케이터 -->
      <div v-if="totalPages > 1" class="slider-indicators">
        <button 
          v-for="index in totalPages" 
          :key="index"
          class="indicator"
          :class="{ active: currentPage === index - 1 }"
          @click="scrollToPage(index - 1)"
        />
      </div>
    </template>
  </section>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { collection, getDocs, query, orderBy, limit, doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore'
import { db } from '@/services/firebase'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'NewJobsSection',
  setup() {
    const sliderRef = ref(null)
    const currentPage = ref(0)
    const cardWidth = 212
    const loading = ref(true)
    const jobs = ref([])
    const userBookmarks = ref([])
    
    const { user } = useAuth()

    const fetchJobs = async () => {
      loading.value = true
      try {
        const q = query(
          collection(db, 'jobs'),
          orderBy('createdAt', 'desc'),
          limit(16)
        )
        const snapshot = await getDocs(q)
        jobs.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (e) {
        console.error('신규 공고 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    const fetchUserBookmarks = async () => {
      if (!user.value) {
        userBookmarks.value = []
        return
      }
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.value.uid))
        if (userDoc.exists()) {
          userBookmarks.value = userDoc.data().bookmarks || []
        }
      } catch (e) {
        console.error('북마크 로드 실패:', e)
        userBookmarks.value = []
      }
    }

    const totalPages = computed(() => {
      return Math.ceil(jobs.value.length / 2)
    })

    const isBookmarked = (jobId) => {
      return userBookmarks.value.includes(jobId)
    }

    const handleToggleBookmark = async (job) => {
      if (!user.value) {
        alert('로그인이 필요합니다')
        return
      }
      
      try {
        const userRef = doc(db, 'users', user.value.uid)
        const jobRef = doc(db, 'jobs', job.id)
        const currentlyBookmarked = isBookmarked(job.id)
        
        if (currentlyBookmarked) {
          await updateDoc(userRef, {
            bookmarks: arrayRemove(job.id)
          })
          await updateDoc(jobRef, {
            bookmarkCount: increment(-1)
          })
          userBookmarks.value = userBookmarks.value.filter(id => id !== job.id)
        } else {
          await updateDoc(userRef, {
            bookmarks: arrayUnion(job.id)
          })
          await updateDoc(jobRef, {
            bookmarkCount: increment(1)
          })
          userBookmarks.value = [...userBookmarks.value, job.id]
        }
      } catch (e) {
        console.error('북마크 토글 실패:', e)
      }
    }

    watch(() => user.value, () => {
      fetchUserBookmarks()
    }, { immediate: true })

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      
      if (hours < 1) return '방금 전'
      if (hours < 24) return `${hours}시간 전`
      if (days < 7) return `${days}일 전`
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    const handleScroll = () => {
      if (sliderRef.value) {
        const scrollLeft = sliderRef.value.scrollLeft
        const page = Math.round(scrollLeft / (cardWidth * 2))
        currentPage.value = Math.min(page, totalPages.value - 1)
      }
    }

    const scrollToPage = (page) => {
      if (sliderRef.value) {
        sliderRef.value.scrollTo({
          left: page * cardWidth * 2,
          behavior: 'smooth'
        })
      }
    }

    onMounted(() => {
      fetchJobs()
    })

    return {
      sliderRef,
      loading,
      jobs,
      currentPage,
      totalPages,
      isBookmarked,
      handleToggleBookmark,
      formatDate,
      handleScroll,
      scrollToPage
    }
  }
}
</script>

<style scoped>
.job-section {
  padding: 48px 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 0 24px;
}

.title-area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  display: flex;
}

.section-icon.new {
  color: #22c55e;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #191f28;
  letter-spacing: -0.5px;
}

.job-count {
  padding: 4px 10px;
  background: #dcfce7;
  color: #22c55e;
  font-size: 13px;
  font-weight: 600;
  border-radius: 12px;
}

/* 로딩 스켈레톤 */
.loading-state {
  padding: 0 24px;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.skeleton-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
}

.skeleton-thumb {
  padding-bottom: 56.25%;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 16px;
}

.skeleton-tags {
  height: 24px;
  width: 60%;
  background: #f2f4f6;
  border-radius: 4px;
  margin-bottom: 10px;
}

.skeleton-title {
  height: 20px;
  width: 80%;
  background: #f2f4f6;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-text {
  height: 16px;
  width: 50%;
  background: #f2f4f6;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-salary {
  height: 18px;
  width: 40%;
  background: #f2f4f6;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* 데스크톱: 그리드 */
.job-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  padding: 0 24px;
}

/* 모바일: 슬라이드 (기본 숨김) */
.job-slider {
  display: none;
}

.slider-indicators {
  display: none;
}

.job-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.card-thumbnail {
  position: relative;
  padding-bottom: 56.25%;
  background: #f2f4f6;
}

.card-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f4f6;
  color: #adb5bd;
}

/* 카드 뱃지 영역 */
.card-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
  max-width: calc(100% - 50px);
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 3px;
}

.badge.premium {
  background: linear-gradient(135deg, #3182f6 0%, #1b64da 100%);
  color: #fff;
  letter-spacing: 0.5px;
}

.badge.pick {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #fff;
}

.badge.urgent {
  background: #ef4444;
  color: #fff;
}

.badge.new {
  background: #22c55e;
  color: #fff;
  letter-spacing: 0.5px;
}

.badge.featured {
  width: 18px;
  height: 18px;
  padding: 0;
  background: transparent;
  color: #3182f6;
}

.badge.featured svg {
  filter: drop-shadow(0 1px 2px rgba(49, 130, 246, 0.3));
}

.bookmark-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  color: #6b7684;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  opacity: 0;
}

.job-card:hover .bookmark-btn {
  opacity: 1;
}

.bookmark-btn:hover {
  background: #fff;
  color: #22c55e;
  transform: scale(1.1);
}

.bookmark-btn svg[fill="currentColor"] {
  color: #22c55e;
}

.card-content {
  padding: 16px;
}

.card-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.tag {
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.tag.region {
  background: #dcfce7;
  color: #22c55e;
}

.tag.industry {
  background: #f2f4f6;
  color: #6b7684;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  letter-spacing: -0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-job-type {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 12px;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-salary {
  font-size: 15px;
  font-weight: 700;
  color: #22c55e;
}

.card-date {
  font-size: 12px;
  color: #8b95a1;
}

/* 태블릿 */
@media (max-width: 1024px) {
  .job-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .loading-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 모바일 */
@media (max-width: 768px) {
  .job-section {
    padding: 32px 0;
  }

  .section-header {
    padding: 0 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .job-grid {
    display: none;
  }

  .loading-state {
    padding: 0 16px;
  }

  .loading-grid {
    display: flex;
    gap: 12px;
    overflow: hidden;
  }

  .skeleton-card {
    flex: 0 0 200px;
  }

  .job-slider {
    display: block;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
  }

  .job-slider::-webkit-scrollbar {
    display: none;
  }

  .slider-track {
    display: flex;
    gap: 12px;
    padding: 0 16px;
  }

  .slider-track .job-card {
    flex: 0 0 200px;
    min-width: 200px;
    scroll-snap-align: start;
  }

  .slider-track .job-card:hover {
    transform: none;
  }

  .slider-track .bookmark-btn {
    display: none;
  }

  .slider-track .card-content {
    padding: 12px;
  }

  .slider-track .card-title {
    font-size: 14px;
  }

  .slider-track .card-job-type {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .slider-track .card-salary {
    font-size: 14px;
  }

  .slider-track .card-tags {
    margin-bottom: 8px;
  }

  .slider-track .tag {
    font-size: 11px;
    padding: 3px 6px;
  }

  .slider-track .card-badges {
    top: 8px;
    left: 8px;
  }

  .slider-track .badge {
    padding: 3px 6px;
    font-size: 10px;
  }

  .slider-track .badge.featured {
    width: 16px;
    height: 16px;
  }

  .slider-indicators {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding-top: 16px;
  }

  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d1d5db;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
  }

  .indicator.active {
    width: 24px;
    border-radius: 4px;
    background: #22c55e;
  }
}
</style>